import {type PresenceStatus} from '#/state/chat-bubble/presence'

const PRESENCE_API_BASE =
  'https://batesky-presence-system.attps.workers.dev'

type HeartbeatResponse = {
  status: PresenceStatus
}

type UserPresence = {
  did: string
  status: PresenceStatus
  lastSeen: number
  privateRoomSlug?: string
}

type BatchStatusResponse = {
  statuses: Record<string, UserPresence>
}

export class PresenceApiClient {
  private baseUrl = PRESENCE_API_BASE

  async sendHeartbeat(did: string): Promise<HeartbeatResponse> {
    return this.post<HeartbeatResponse>(
      `${this.baseUrl}/api/presence/heartbeat`,
      {did},
    )
  }

  async getStatuses(dids: string[]): Promise<BatchStatusResponse> {
    if (dids.length === 0) {
      return {statuses: {}}
    }
    const params = new URLSearchParams({dids: dids.join(',')})
    return this.get<BatchStatusResponse>(
      `${this.baseUrl}/api/presence/status?${params}`,
    )
  }

  async setPrivateRoom(did: string, roomSlug: string): Promise<void> {
    await this.post(
      `${this.baseUrl}/api/presence/private-room`,
      {did, roomSlug},
    )
  }

  async clearPrivateRoom(did: string): Promise<void> {
    await this.post(
      `${this.baseUrl}/api/presence/private-room`,
      {did, roomSlug: ''},
    )
  }

  getWebSocketUrl(did: string): string {
    const wsBase = this.baseUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://')
    return `${wsBase}/api/presence/subscribe?did=${encodeURIComponent(did)}`
  }

  private async post<T>(url: string, data: unknown): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Presence API error (${response.status}): ${errorText || response.statusText}`,
      )
    }

    return response.json()
  }

  private async get<T>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {'Content-Type': 'application/json'},
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Presence API error (${response.status}): ${errorText || response.statusText}`,
      )
    }

    return response.json()
  }
}

export const presenceApiClient = new PresenceApiClient()
